import {Component, effect, inject, OnDestroy, OnInit, ViewContainerRef} from '@angular/core';
import {ActivatedRoute, NavigationEnd, RouterOutlet} from '@angular/router';
import {Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {AppAuthService} from '@services/core/auth/app-auth.service';
import {LocalizeService} from '@services/helpers/localize/localize.service';
import {ModalService} from '@services/helpers/modal/modal.service';
import {
  Dashboard,
  DashboardLayout,
} from '@models/app/dashboard-layouts/DashboardLayout';
import {TenantService} from '@services/core/tenant/tenant.service';
import {Subject} from 'rxjs/internal/Subject';
import {takeUntil} from 'rxjs/operators';
import {ThemeService} from '@services/core/theme/theme.service';

import {AlertComponent} from "@shared/components/alert/alert.component";
import {WebsocketService} from "@app/areas/real-time/services/websocket.service";
import {ChatAuthService} from "@app/areas/chat/services/chat-auth.service";
import {ChatApiService} from "@app/areas/chat/services/chat-api.service";
import {DeviceIdService} from "@app/areas/device/services/device-id.service";
import {PractitionerUserData} from "@models/data/auth/AuthPractitionerUserData";
import { EnquiriesService } from './areas/crm/enquiries/services/enquiries.service'
import {UserOnboardingService} from "@app/areas/account/onboarding/services/user-onboarding.service";
import {
  MyOnboardingProgressComponent
} from "@app/areas/account/onboarding/components/my-onboarding-progress/my-onboarding-progress.component";
import { ImpersonationStateService } from '@app/areas/auth/services/impersonation-state.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [
    RouterOutlet,
    AlertComponent,
    MyOnboardingProgressComponent,
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly chatAuth = inject(ChatAuthService);
  private unsub = new Subject<void>();
  loading = true;

  userDashboard = {
    showClinician: false,
    showDataManagement: false,
  };

  onboarding = inject(UserOnboardingService)
  impersonation = inject(ImpersonationStateService);

  constructor(
    public modalService: ModalService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    public appAuthService: AppAuthService,
    private localize: LocalizeService,
    private vcr: ViewContainerRef,
    private tenantService: TenantService,
    private theme: ThemeService,
    private ws: WebsocketService,
    private chatApi: ChatApiService,
    private device: DeviceIdService,
    private enquiries: EnquiriesService,
  ) {
    // subscribe to the user notification topic
    effect(() => {
      const u = this.appAuthService.user$()

      console.log('User:', u)

      if (u !== null) {
        console.log('USER CHANGED')
        this.handleUserChanged(u)
        this.impersonation.syncState();
      } else {
        this.handleUserNotLoggedIn();
        this.impersonation.state$.set({ active: false });
      }
    })

    // subscribing to the theme change after the DOM has been loaded in
    this.theme.theme$
      .pipe(takeUntil(this.unsub))
      .subscribe(t => {
        switch (t) {
          case 'dark':
            document.documentElement.classList.add('dark');
            break;
          case 'light':
            document.documentElement.classList.remove('dark');
            break;
        }
      })

    // Loading subscription
    this.appAuthService.loading$
      .pipe(takeUntil(this.unsub))
      .subscribe((loading) => this.handleUserLoading(loading));

    const appTitle = 'FitMed';
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        const fromChild = this.getTitleFromChild(this.activatedRoute.firstChild);
        const title = this.localize.l(
          fromChild,
          fromChild,
        );
        this.titleService.setTitle(title ? appTitle + ' | ' + title : appTitle);
      }
    });
  }

  ngOnInit(): void {
    this.setModalContainerReference();
  }

  ngOnDestroy(): void {
    this.unsub.next();
    this.unsub.complete();
  }

  private handleUserLoading(loading: boolean): void {
    this.showHideLoader(loading);
    this.setupDefaultTenant(loading);
    setTimeout(() => {
      this.loading = loading;
    });
  }

  private initialisedUser : string | null = null

  private handleUserChanged(u: PractitionerUserData): void {
    const deviceId = this.device.get();
    const userId = u?.identityProfile?.id ?? '';

    // do not re-initialise the same user
    if (this.initialisedUser === userId) {
      return;
    }

    this.initialisedUser = userId
    this.enquiries.refresh();

    // rotate the user key
    this.chatAuth.ensureDeviceKeyOnServer(this.chatApi, deviceId)
      .then()
      .catch()

    this.onboarding.refresh()
  }

  private handleUserNotLoggedIn(): void {
    this.ws.close()
    this.initialisedUser = null;
  }

  private getTitleFromChild(child, n = 0): string {
    if (child) {
      const title = child.snapshot.data.title;
      if (!title && n <= 5) {
        return this.getTitleFromChild(child.firstChild, n + 1);
      }
      return title ? title : '';
    }
    return '';
  }

  private setModalContainerReference(): void {
    this.modalService.setVcr = this.vcr;
  }

  private showHideLoader(loading): void {
    const element = document.getElementById('pageLoader');
    if (loading) {
      element.style.display = 'block'
      // element.setAttribute('style', 'display:block !important');
    } else {
      element.style.display = 'none'
      // element.setAttribute('style', 'display:none !important');
    }
  }

  private setupDefaultTenant(isLoadingAuth: boolean): void {
    if (!isLoadingAuth && this.appAuthService.loggedIn) {
      this.tenantService.init();
    }
  }

  private setupSelectedDashboard(selectedDashboard: DashboardLayout): void {
    this.userDashboard = {
      showClinician: (selectedDashboard?.type ?? null) === Dashboard.Clinician,
      showDataManagement:
        (selectedDashboard?.type ?? null) === Dashboard.DataManagement,
    };
  }
}
